import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "./authenticatedFetch";

export interface PushbulletDevice {
  id: string;
  organizationId: string;
  connectorId: string;
  connectorName?: string;
  deviceIden: string;
  nickname?: string;
  type?: string;
  verifiedAt: string;
}

export interface PushbulletApiDevice {
  iden: string;
  nickname?: string;
  type?: string;
  active?: boolean;
}

export interface PaginatedResponse<T> {
  items: T[];
  limit: number;
  offset: number;
  hasMore: boolean;
}

interface ApiResponse<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
}

async function unwrap<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(errorData.message || response.statusText);
  }
  const data = (await response.json()) as ApiResponse<T>;
  if (data.code !== 0) {
    throw new Error(`API error: ${data.message}`);
  }
  return data.data;
}

export async function listPushbulletDevices(
  connectorId?: string,
  limit = 50,
  offset = 0,
): Promise<PaginatedResponse<PushbulletDevice>> {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  if (connectorId) params.set("connector_id", connectorId);

  const response = await authenticatedFetch<ApiResponse<PaginatedResponse<PushbulletDevice>>>(
    `${V2_ENDPOINT}/v1/pushbullet/devices?${params.toString()}`,
  );
  return unwrap<PaginatedResponse<PushbulletDevice>>(response);
}

export async function upsertPushbulletDevice(input: {
  connectorId: string;
  deviceIden: string;
  nickname?: string;
  type?: string;
}): Promise<{ device: PushbulletDevice }> {
  const response = await authenticatedFetch<ApiResponse<{ device: PushbulletDevice }>>(
    `${V2_ENDPOINT}/v1/pushbullet/devices`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    },
  );
  return unwrap<{ device: PushbulletDevice }>(response);
}

export async function deletePushbulletDevice(id: string): Promise<void> {
  const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/pushbullet/devices/${id}`, { method: "DELETE" });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(errorData.message || response.statusText);
  }
}

/**
 * Fetch the user's Pushbullet devices directly from the Pushbullet API using
 * an access token they just pasted. Used during connector setup so devices
 * can be auto-imported into our destinations table without having to decrypt
 * the connector data server-side.
 */
export async function fetchPushbulletDevicesFromApi(accessToken: string): Promise<PushbulletApiDevice[]> {
  const response = await fetch("https://api.pushbullet.com/v2/devices", {
    headers: { "Access-Token": accessToken },
  });
  if (!response.ok) {
    throw new Error(`Pushbullet API ${response.status}: ${response.statusText}`);
  }
  const data = (await response.json()) as { devices?: PushbulletApiDevice[] };
  return (data.devices ?? []).filter(d => d.active !== false);
}
